import { Box, Typography } from "@mui/material";
import { Groups } from "../../../components/groups/Groups";
import { useRecordsContext } from "../../../utils/useRecordsContext";
import { IntakeFormAdditionalInfo } from "./IntakeFormAdditionalInfo";
import { useEffect, useMemo, useRef, useState } from "react";
import { findField, findFieldGroup } from "../../../utils/findField";
import { GroupsOptions } from "../../../types/groups.types";
import { normalizeFormCheckboxAnswer } from "../../../utils/textUtils";
import { addHours, format, isAfter } from "date-fns";
import { getArrayElementByDateProperty } from "../../../utils/dateUtils";

type IntakeFormProps = {
    doneReportSessionDate?: string;
};

export const IntakeForm = ({ doneReportSessionDate }: IntakeFormProps) => {
    const { entity, items, formatedFields, refetch } = useRecordsContext("relatedRecords");

    const selectedForm = useMemo(
        () => findLastFormsBeforeDoneReportSession(items, doneReportSessionDate),
        [items, doneReportSessionDate]
    );

    // The following logic is needed in case the IntakeFormAdditionalInfo height becomes bigger than the Groups
    const rightRef = useRef<HTMLDivElement>(null);
    const [rightHeight, setRightHeight] = useState<number | null>(null);
    const rightHeightBaseValue = 636; // default value of rightside box height, with ckeditor in edit mode

    useEffect(() => {
        if (!rightRef.current) return;
        const observer = new ResizeObserver(() => {
            setRightHeight(rightRef.current!.clientHeight);
        });
        observer.observe(rightRef.current);
        return () => observer.disconnect();
    }, []);

    if (!Array.isArray(items) || items.length === 0 || !selectedForm || !refetch) return null;

    const NUMBER_OF_CHARACTERS = 50000; // to make sure it displays all the text

    const groupsOptions: GroupsOptions = {
        fieldsWithExtraOptions: [
            {
                field: "diet_exercise_sleep",
                charactersLimit: NUMBER_OF_CHARACTERS,
                formatForDisplay: normalizeFormCheckboxAnswer,
            },
            {
                field: "skills_social",
                charactersLimit: NUMBER_OF_CHARACTERS,
                formatForDisplay: normalizeFormCheckboxAnswer,
            },
            {
                field: "work_study_details",
                charactersLimit: NUMBER_OF_CHARACTERS,
                formatForDisplay: normalizeFormCheckboxAnswer,
            },
            {
                field: "school_difficulty_indicators",
                charactersLimit: NUMBER_OF_CHARACTERS,
                formatForDisplay: normalizeFormCheckboxAnswer,
            },
            {
                field: "about_pregnancy",
                charactersLimit: NUMBER_OF_CHARACTERS,
                formatForDisplay: normalizeFormCheckboxAnswer,
            },
            {
                field: "other_healthcare_services",
                charactersLimit: NUMBER_OF_CHARACTERS,
                formatForDisplay: normalizeFormCheckboxAnswer,
            },
            {
                field: "birthdate",
                dateFormat: "dd/MM/yyyy",
            },
            {
                field: "client_added_info",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "assessment_reason",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "age_at_diagnosis",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "difficulty_duration",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "medications",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "psychiatric_psychological_care",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "clinical_diagnosis_details",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "specialties_by_diagnosis",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "sibling_related_diagnoses",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "failed_school_years",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "failed_subjects",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
            {
                field: "nervous_tics",
                charactersLimit: NUMBER_OF_CHARACTERS,
            },
        ],
    };

    const groupOfFieldContent = findFieldGroup(formatedFields, "content");
    const severityPatientField = findField(formatedFields, "severity_patient");

    return (
        <Box sx={{ height: "100%", width: "100%", display: "flex", flex: 1, flexDirection: "column", mb: 4 }}>
            <Box display="flex" flexDirection={{ xs: "column", md: "row" }} alignItems="stretch">
                <Box width={{ sm: "70%", md: "70%" }}>
                    <Groups
                        item={selectedForm}
                        fields={formatedFields.filter((f) => f.field !== groupOfFieldContent?.field)}
                        groupsOptions={groupsOptions}
                    />
                </Box>
                <Box
                    ref={rightRef}
                    sx={{
                        width: { sm: "30%", md: "30%" },
                        minWidth: "250px",
                        position: "sticky",
                        top: 60,
                        height: "fit-content",
                        maxHeight: "calc(100vh - 60px)",
                        overflowY: "auto",
                        pb: 2,
                    }}
                >
                    <IntakeFormAdditionalInfo
                        item={selectedForm}
                        entity={entity}
                        field={groupOfFieldContent}
                        severityPatientField={severityPatientField}
                        refetch={refetch}
                    />
                </Box>
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1, ml: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                ID do questionário: {selectedForm.id} - criado em: {format(new Date(selectedForm.date_created), "dd/MM/yyyy HH:mm")}
            </Typography>
            {items.length > 1 && (
                <Typography variant="body2" color="text.secondary">
                    O cliente preencheu o questionário múltiplas vezes. É apresentado o último questionário preenchido até à data de fim da Consulta de Avaliação realizada.
                    </Typography>
                )}
            </Box>
            {rightHeight && <Box sx={{ height: `calc(${rightHeight}px - ${rightHeightBaseValue}px)` }} />}
        </Box>
    );
};

export const findLastFormsBeforeDoneReportSession = (
    intakeForms: Array<any> | undefined,
    doneReportSessionDate: string | undefined
) => {
    if (!intakeForms || intakeForms.length === 0) return null;

    if (!doneReportSessionDate) {
        return getArrayElementByDateProperty(intakeForms, "date_created", true);
    }

    const cappedForms = intakeForms.filter(
        (form: any) => !isAfter(new Date(form.date_created), addHours(new Date(doneReportSessionDate), 1))
    );

    const formsToPickFrom = cappedForms.length > 0 ? cappedForms : [intakeForms[0]];
    const mostRecentForm = getArrayElementByDateProperty(formsToPickFrom, "date_created", true);

    return mostRecentForm ?? formsToPickFrom[0];
};